import cx from 'classnames'
import { Field, FieldValidator } from 'formik'
import antigenInvalid from 'images/antigen-inv.svg'
import antigenNeg from 'images/antigen-neg.svg'
import antigenPos1 from 'images/antigen-pos1.svg'
import antigenPos2 from 'images/antigen-pos2.svg'

const Quoted = ({ children }: { children: React.ReactNode }) => (
  <span className="has-text-primary">&lsquo;{children}&rsquo;</span>
)

interface AntigenResultInputFieldProps {
  validate?: FieldValidator
}

const AntigenResultInputField = ({ validate }: AntigenResultInputFieldProps) => {
  const name = 'result'

  return (
    <Field name={name} validate={validate}>
      {({ meta, field }) => (
        <div className="is-size-7 mb-4">
          <p className="label is-size-7 has-text-weight-light mb-3">Your test result</p>

          <div className="columns">
            <div className="column">
              <label
                className={cx('result-option is-flex is-align-items-center', {
                  'is-selected': field.value === 'NEGATIVE',
                  'is-danger': meta.touched && meta.error,
                })}
              >
                <Field type="radio" name={name} value="NEGATIVE" />
                <div className="ml-4">
                  <div>
                    <img src={antigenNeg} alt="Negative diagram" width="100px" />
                  </div>

                  <p className="mt-2">
                    <strong>Negative</strong>: A <Quoted>C</Quoted> line but no <Quoted>T</Quoted> line.
                  </p>
                </div>
              </label>
            </div>

            <div className="column">
              <label
                className={cx('result-option is-flex is-align-items-center', {
                  'is-selected': field.value === 'POSITIVE',
                  'is-danger': meta.touched && meta.error,
                })}
              >
                <Field type="radio" name={name} value="POSITIVE" />

                <div className="ml-4">
                  <div className="is-flex is-flex-wrap-wrap is-align-items-center">
                    <img src={antigenPos1} alt="Positive diagram" width="100px" />
                    <span className="mx-1">or</span>
                    <img src={antigenPos2} alt="Positive diagram 2" width="100px" />
                  </div>

                  <p className="mt-2">
                    <strong>Positive</strong>: <Quoted>C</Quoted> and <Quoted>T</Quoted> lines are both present. Even if
                    the <Quoted>T</Quoted> line is very faint it's still a positive test as long as there's a{' '}
                    <Quoted>C</Quoted> line as well.
                  </p>
                </div>
              </label>
            </div>

            <div className="column">
              <label
                className={cx('result-option is-flex is-align-items-center', {
                  'is-selected': field.value === 'INVALID',
                  'is-danger': meta.touched && meta.error,
                })}
              >
                <Field type="radio" name={name} value="INVALID" />
                <div className="ml-4">
                  <div>
                    <img src={antigenInvalid} alt="Invalid diagram" width="100px" />
                  </div>

                  <p className="mt-2">
                    <strong>Invalid</strong>: No <Quoted>C</Quoted> line visible. This is rare but it can happen and
                    you'll need to re-test.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {meta.touched && meta.error && <p className="help is-danger">{meta.error}</p>}
        </div>
      )}
    </Field>
  )
}

export default AntigenResultInputField
